import { isRejectedWithValue } from "@reduxjs/toolkit";
import { format } from "date-fns";
import { getResourcesFree, getResourcesState, selecResourcesFree } from "entities/hallmap";
import { selectResources, selectResourcesState } from "entities/hallmap/model/selectors";
import { Resource, ResourcesState } from "entities/hallmap/model/types";
import { clearLoadedTable, resetBookingData, resetOrderDetail, resetOrderDetailPerson, selectOrderBookingData, setBookingThreadMod, toBook } from "entities/orders";
import { OrderBookingData, PostOrderResponce } from "entities/orders/model/types";
import { resetPersonDetail, selectDefaultPerson } from "entities/person";
import { selectSettings } from "entities/settings/model/selector";
import { selectPlaces } from "entities/toolbar/places";
import { getBookingTimeslots, getTimeslots, selectBookingTimes, selectSelectedTimeslot, useCurrentTimeslot } from "entities/toolbar/timeslots";
import { TimeslotsBookingParams } from "entities/toolbar/timeslots/model/types";
import { useBookingDuration } from "features/booking/bookingDateTime/lib/useBookingDuration";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { CustomError } from "shared/api";
import { showError } from "shared/lib/asyncActionFeedback";
import { getRandomUUID } from "shared/lib/core";
import { getDigFromString } from "shared/lib/core/lib/utils";
import { formatWithLocale } from "shared/lib/datepicker";
import { useAppDispatch, useAppSelector } from "shared/lib/store";
import { timeToMinutes } from "shared/lib/time";
import { Option } from "shared/types";

export type FilterValues = {
  quantity: number | string;
  duration: Option
}

export type resourceDopType = {
  max_capacity: number
}
export type TResource = Partial<Resource & ResourcesState & resourceDopType>;

export interface IHall {
  name: string,
  items: TResource[]
}

export const useMobilePlace = (isLoaded: boolean) => {
  const dispatch = useAppDispatch();

  const appSettings = useAppSelector(selectSettings);
  const selectedTimeslot = useAppSelector(selectSelectedTimeslot);
  const places = useAppSelector(selectPlaces);
  const resources = useAppSelector(selectResources);
  const resourcesState = useAppSelector(selectResourcesState);
  const resourcesFree = useAppSelector(selecResourcesFree);
  const bookingData = useAppSelector(selectOrderBookingData);
  const defaultPerson = useAppSelector(selectDefaultPerson);
  const bookingTimes = useAppSelector(selectBookingTimes);

  const { toCurrentTimeslot, getCurrentTimeSlot } = useCurrentTimeslot();
  const { durationOption, setDurationByTime, getMinDuration } = useBookingDuration();

  const loadStateTimeoutId = useRef<NodeJS.Timeout | null>(null);
  const idempotencyKeyRef = useRef(crypto?.randomUUID ? crypto.randomUUID() : getRandomUUID())

  
  const { control, setValue, getValues } = useForm<FilterValues>({
    defaultValues: {
      quantity: '2',
      duration: durationOption ? durationOption : {"label": "01:45","value": "01:45"}
    },
  });

  const [ timeString, setTimeString ] = useState<string>('00:00');
  const [ isLoading, setIsLoading ] = useState<boolean>(true);
  const [ renderTables, setRenderTables ] = useState<IHall[] | null>(null);
  const [ activeTables, setActiveTables ] = useState<TResource[]>([])
  const [ isHandMode, setIsHandMode ] = useState<boolean>(false);
  const [ buttonDisabled, setButtonDisabled ] = useState<boolean>(true);
  const [ noTablesText, setNoTablesText ] = useState<string>('Нет свободных столов')

  const minDuration = useRef(30);

  const getCurrTimeSlot = useCallback(()=>{
    if (selectedTimeslot?.trim()) {
      return selectedTimeslot
    }

    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const date = currentDate.getDate();
    const hours = currentDate.getHours();
    let minutes = currentDate.getMinutes();

    if (minutes < 15) {
      minutes = 0
    }
    if (minutes > 15 && minutes < 30) {
      minutes = 15
    }
    if (minutes > 30 && minutes < 45) {
      minutes = 30
    }
    if (minutes > 45 && minutes <= 59) {
      minutes = 45
    }
    // if (minutes > 45) {
    //   ++hours;
    //   minutes = 0
    // }
    const today = new Date(year, month, date, hours, minutes)

    const offset = today.getTimezoneOffset();
    const offsetHours = Math.abs(offset) / 60;
    const offsetMinutes = Math.abs(offset) % 60;
    const offsetStr = `${offset > 0 ? '-' : '+'}${offsetHours > 9 ? offsetHours : `0${offsetHours}`}:${offsetMinutes > 9 ? offsetMinutes : `0${offsetMinutes}`}`;
    const str = format(today, `yyyy-MM-dd'T'HH:mm:ss${offsetStr}`)

    return str;
  }, [selectedTimeslot])

  const setMinDuration = useCallback(() => {
    const hours = new Date(getCurrTimeSlot()).getHours();
    const minutes = new Date(getCurrTimeSlot()).getMinutes();
    setTimeString(`${hours > 9 ? hours : `0${hours}`}:${minutes > 9 ? minutes : `0${minutes}`}`);
    
    const minDur = getMinDuration(timeString);

    if (minDur > 0) {
      minDuration.current = minDur;
    }
  }, [getCurrTimeSlot, setTimeString, getMinDuration, minDuration, timeString])

  const toBooking = useCallback(async () => {
    await dispatch(resetBookingData());
    await dispatch(resetOrderDetail());
    await dispatch(resetOrderDetailPerson());
    await dispatch(resetPersonDetail());
    await dispatch(clearLoadedTable());
  }, [dispatch])

  const accomodateGuests = useCallback(async () => {
    await toCurrentTimeslot();
    await dispatch(setBookingThreadMod({ isWork: true }));
    setTimeout(() => {
      toBooking();
    }, 200)
  }, [dispatch, toCurrentTimeslot, toBooking])
  
  const loadState = useCallback(async () => {
    if (!getCurrTimeSlot()?.trim()) {
      return;
    }

    await dispatch(getTimeslots({date_slot: formatWithLocale(new Date(), 'yyyy-MM-dd')}));

    await dispatch(
      getResourcesState({ dt_requested: getCurrTimeSlot() }),
    );
  }, [getCurrTimeSlot, dispatch])

  const loadFrees = useCallback(async ()=>{
    const freeOpts = {
      dt_requested_start: getCurrTimeSlot(),
      duration: timeToMinutes(getValues().duration.value) ? timeToMinutes(getValues().duration.value) : 105,
    }
    await dispatch(getResourcesFree(freeOpts))
  },[getCurrTimeSlot, dispatch, getValues])

  const loadFreeSlots = useCallback(async () => {
    const opts: TimeslotsBookingParams = {
      "count": Number(getValues().quantity),
      "duration": timeToMinutes(getValues().duration.value),
      "dt_slot": getCurrTimeSlot(),
      "bypass": isHandMode
    }

    await dispatch(getBookingTimeslots(opts))
  }, [dispatch, getValues, getCurrTimeSlot, isHandMode])

  const onPlaceSelectChange = useCallback(async () => {
    await loadFreeSlots();
    await accomodateGuests();
    await loadState();
    await loadFrees();

    loadStateTimeoutId.current && clearInterval(loadStateTimeoutId.current);
    loadStateTimeoutId.current = setInterval(async () => {
      await loadState();
      await loadFrees();
    }, 5000);
  }, [accomodateGuests, loadState, loadFrees, loadFreeSlots]);

  const setTables = useCallback(async (isEffect: boolean = false)=>{
    if (!resourcesFree.length) {
      setTimeout(() => {
        onPlaceSelectChange();
      }, 1000);

      return;
    }

    if (isEffect) {
      await loadFreeSlots();
    }
    
    if (bookingTimes[0]&&!bookingTimes[0]?.hasSeats) {
      setActiveTables([]);
      setButtonDisabled(true);
      setRenderTables([]);
      setIsLoading(false);
      setNoTablesText('Сейчас размещение невозможно: превышен лимит гостей');
      return;
    }

    const resourcesFiltered = resources.map((resource: TResource) => {
      const inState = resourcesState.find(el=>el.id===resource.id);
      return {
        ...inState,
        ...resource
      };
    }).filter((resource: TResource)=>{
      const condition = resourcesFree.find(el=>el.id === resource.id)&&resource.state === 'free';

      if (condition) {
        return true;
      }
      return false;
    });
    const halls: IHall[] = [];

    for (let index = 0; index < resourcesFiltered.length; index++) {
      const table = resourcesFiltered[index];
      const place = table.place;
      const placeName = places.find(el=>el.id===place)?.name;
      if (!placeName) continue;

      let hallIdx = halls.findIndex(el=>el.name === placeName);
      if (hallIdx < 0) {
        halls.push({name: placeName, items: []})
        hallIdx = halls.length - 1;
      }
      
      halls[hallIdx].items.push(table);
    }

    if (!isHandMode&&!isEffect) {
      setActiveTables([]);
      setButtonDisabled(true);
    }
    if (!isHandMode||isEffect) {
      setRenderTables(halls)
      if (!halls.length) {
        setNoTablesText('Нет свободных столов');
      }
    }

    setIsLoading(false);
    
    // eslint-disable-next-line
  }, [
    places, 
    resources, 
    getValues, 
    dispatch, 
    resourcesFree, 
    selectedTimeslot, 
    isHandMode,
    toCurrentTimeslot,
    getCurrentTimeSlot,
    bookingTimes
  ])

  useEffect(()=>{
    if (isLoaded) {
      loadStateTimeoutId.current && clearInterval(loadStateTimeoutId.current);
      onPlaceSelectChange()
    }
    // eslint-disable-next-line
  }, [loadStateTimeoutId, isLoaded])

  useEffect(()=>{
    if (places?.length&&resourcesState?.length&&isLoading) {
      getCurrTimeSlot();
      const hours = new Date(getCurrTimeSlot()).getHours();
      const minutes = new Date(getCurrTimeSlot()).getMinutes();
      setTimeString(`${hours > 9 ? hours : `0${hours}`}:${minutes > 9 ? minutes : `0${minutes}`}`);
      const option = setDurationByTime(timeString);
      setValue('quantity', appSettings?.default_participants_count || 2);

      if (durationOption) {
        setValue('duration', durationOption)
      } else {
        setValue('duration', option || {"label": "01:45","value": "01:45"})
      }

      setTables();
    }

    // eslint-disable-next-line
  }, [getCurrTimeSlot, places, resourcesState, setValue, isLoading])

  const onTableClick = useCallback((table: TResource)=>{
    if (!table) return;
    const arr = activeTables;
    const id = table.id;

    const findedIndex = arr.findIndex(el=>el.id===id);
    if (findedIndex >= 0) {
      arr.splice(findedIndex, 1);
    } else {
      arr.push(table)
    }

    setActiveTables(arr);
    setButtonDisabled(arr.length <= 0)

  }, [activeTables, setActiveTables, setButtonDisabled])

  useEffect(()=>{
    if (activeTables.length&&(getDigFromString(getValues().quantity)>0||getValues().duration.value!=='00:00')) {
      setButtonDisabled(false);
    } else {
      setButtonDisabled(true);
    }

  }, [activeTables, getValues])

  useEffect(() => {
    setTables(true)
    // eslint-disable-next-line
  }, [isHandMode, resourcesState, resourcesFree])

  useEffect(()=>{
    setMinDuration();
  }, [selectedTimeslot, setMinDuration])

  const removeRestrictions = useCallback(async ()=>{
    setActiveTables([]);
    setButtonDisabled(true);
    setValue('duration', durationOption ? durationOption : {"label": "01:45","value": "01:45"});
    setValue('quantity', appSettings?.default_participants_count || 2);
    await onPlaceSelectChange();
  },[
    setActiveTables, 
    setButtonDisabled, 
    setValue,
    appSettings?.default_participants_count,
    durationOption,
    onPlaceSelectChange
  ])

  const setIdempotencyKey = useCallback(() => {
    idempotencyKeyRef.current = crypto?.randomUUID ? crypto.randomUUID() : getRandomUUID();
  }, [idempotencyKeyRef])

  const handleBookingStatusCheck = useCallback(async (prepareData: OrderBookingData, timeoutId?:NodeJS.Timeout, toastId?: string|number) => {
    setIsLoading(true);
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const bookResult = await dispatch(toBook(prepareData))

    if (isRejectedWithValue(bookResult)) {
      showError(bookResult.payload as CustomError);
      removeRestrictions();
      return;
    }

    const response = bookResult.payload as PostOrderResponce;
    const responseStatus = response?.status;
    if (responseStatus) {
      toast.dismiss(toastId);
      clearTimeout(timeoutId);
      removeRestrictions();
      setIdempotencyKey();

      if (responseStatus === 1) {
        toast.success(`Размещение успешно зарегистрировано в системе`);
      } else if (responseStatus === 99) {
        if (response?.errors) {
          toast.error(`К сожалению, не удалось создать бронирование по следующей причине: "${response?.errors}". Попробуйте, пожалуйста, изменить время, продолжительность или забронированные ресурсы!`)
        }
      }
    } else {
      timeoutId = setTimeout(() => {
        handleBookingStatusCheck({...prepareData, idempotencyKey: idempotencyKeyRef.current} as OrderBookingData, timeoutId);
      }, 1000);
    }
  }, [
    setIsLoading,
    dispatch,
    removeRestrictions,
    setIdempotencyKey
  ])

  const onBook = useCallback(async ()=>{
    getCurrTimeSlot();
    if (!defaultPerson||!activeTables.length) return;

    const resources = activeTables.map(el=>el.id || 0).filter(el=>el !== 0);

    const participants_count = getDigFromString(getValues().quantity);

    if (participants_count <= 0) return toast.warning('Укажите количество гостей');
    if (!resources?.length) return  toast.warning('Не выбраны столы');

    const prepareData = {
      ...bookingData,
      auto_start_visit: true,
      dt_start: getCurrTimeSlot(),
      duration: timeToMinutes(getValues().duration.value),
      participants_count,
      resources,
      idempotencyKey: idempotencyKeyRef.current,
      bypass: isHandMode,
      person: {
        tags: defaultPerson.tags,
        name: defaultPerson.name,
        phone: defaultPerson.phone,
        notes: defaultPerson.notes,
        visits: defaultPerson.visits,
        cancelled: defaultPerson.cancelled,
        dt_last_visit: defaultPerson.dt_last_visit,
        id: defaultPerson.id,
      }
    }
    
    const toastId = toast.info(
      'Проверяем доступность ресурсов...',
      { autoClose: false },
    );
    handleBookingStatusCheck(prepareData, undefined, toastId)
  }, [handleBookingStatusCheck, getValues, getCurrTimeSlot, bookingData, activeTables, defaultPerson, idempotencyKeyRef, isHandMode])


  return {
    control,
    renderTables,
    isLoading,
    setValue,
    getValues,
    setTables,
    onTableClick,
    activeTables,
    isHandMode,
    setIsHandMode,
    onPlaceSelectChange,
    buttonDisabled,
    onBook,
    minDuration: minDuration.current,
    loadFreeSlots,
    noTablesText
  }
}